import {MemoizedSelector} from '@ngrx/store';

/**
 * Utility-Klasse für das Caching von parametrisierten Selektoren.
 * Verhindert, dass bei jedem Aufruf neue Selector-Instanzen erstellt werden.
 * 
 * @example
 * ```typescript
 * export class MySelectors {
 *   private static readonly cache = new SelectorCache<MyItem>();
 *   
 *   static readonly itemById = (id: string) => 
 *     this.cache.getOrCreate(`item_${id}`, () => 
 *       createSelector(feature, state => state.entities[id])
 *     );
 * }
 * ```
 */
export class SelectorCache<T> {
  private readonly cache = new Map<string, MemoizedSelector<any, T>>();
  
  /**
   * Gibt einen gecachten Selector zurück oder erstellt einen neuen.
   * 
   * @param key - Eindeutiger Cache-Key
   * @param factory - Factory-Funktion die den Selector erstellt
   * @returns Der gecachte oder neu erstellte Selector
   */
  getOrCreate(key: string, factory: () => MemoizedSelector<any, T>): MemoizedSelector<any, T> {
    if (!this.cache.has(key)) {
      this.cache.set(key, factory());
    }
    return this.cache.get(key)!;
  }
  
  /**
   * Leert den Cache. Nützlich für Tests.
   */
  clear(): void {
    this.cache.clear();
  }
  
  /**
   * Gibt die Anzahl der gecachten Selektoren zurück.
   */
  get size(): number {
    return this.cache.size;
  }
}
